import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { STATIC_SOCIETIES } from "@/data/societies";
import Icon from "@/components/Icon";
import AdminEmptyState from "@/components/AdminEmptyState";

const PAGE_SIZE = 15;

/**
 * Admin societies list: debounced search + infinite scroll pagination.
 * The list is baked into the frontend (STATIC_SOCIETIES) so no API call
 * is made on load. Clicking a society opens its membership approval screen.
 */
const SocietiesPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [page, setPage] = useState(1);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Filter the baked-in list by the (debounced) search query.
  const filteredSocieties = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return STATIC_SOCIETIES;
    return STATIC_SOCIETIES.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        (s.university ?? "").toLowerCase().includes(q),
    );
  }, [searchQuery]);

  // Paginate locally (infinite scroll over the static list).
  const visibleSocieties = useMemo(
    () => filteredSocieties.slice(0, page * PAGE_SIZE),
    [filteredSocieties, page],
  );

  const hasMore = visibleSocieties.length < filteredSocieties.length;
  const endReached = !hasMore;

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // Reset pagination whenever the search query changes.
  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  const handleSearchChange = (text: string) => {
    setInputValue(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchQuery(text);
    }, 300);
  };

  const loadMore = useCallback(() => {
    if (!hasMore || endReached) return;
    setPage((prev) => prev + 1);
  }, [hasMore, endReached]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
      loadMore();
    }
  }, [loadMore]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  return (
    <div className="flex min-h-full flex-col p-4">
      <h1 className="mb-4 text-2xl font-bold text-[#222]">{t("Societies")}</h1>

      <div className="mb-3 flex w-full items-center rounded-[10px] border border-[#e0e0e0] bg-white px-3 py-2.5">
        <Icon iconType="Ionicons" name="search" size={18} color="#999" />
        <input
          className="ml-2 flex-1 text-sm text-[#333] focus:outline-none"
          placeholder={t("Search societies...")}
          value={inputValue}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
        {searchQuery.length > 0 && (
          <button type="button" onClick={() => handleSearchChange("")}>
            <Icon iconType="Ionicons" name="close-circle" size={18} color="#999" />
          </button>
        )}
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto pb-4">
        {visibleSocieties.length === 0 ? (
          <AdminEmptyState
            iconType="MaterialCommunityIcons"
            iconName="account-group-outline"
            title={t("No societies found")}
            message={t("Try a different search.")}
            actionLabel={t("Refresh")}
            onAction={() => handleSearchChange("")}
          />
        ) : (
          <>
            <div className="w-full">
              {visibleSocieties.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  className="mb-2.5 flex w-full items-center rounded-xl bg-white p-3.5 text-left shadow-sm transition hover:shadow"
                  onClick={() =>
                    navigate(`/console/societies/${s.id}?name=${encodeURIComponent(s.name || "")}`)
                  }
                >
                  {s.icon ? (
                    <img
                      src={s.icon}
                      alt=""
                      className="mr-3 h-[46px] w-[46px] rounded-lg object-cover"
                    />
                  ) : (
                    <div className="mr-3 flex h-[46px] w-[46px] items-center justify-center rounded-lg bg-[#F0F6FF]">
                      <Icon
                        iconType="MaterialCommunityIcons"
                        name="account-group"
                        size={26}
                        color="#ffa217"
                      />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[15px] font-semibold text-[#222]">
                      {s.name || "-"}
                    </div>
                    <div className="mt-0.5 text-xs text-[#888]">
                      {`${s.memberCount ?? 0} ${t("Members")}`}
                    </div>
                  </div>
                  <Icon
                    iconType="MaterialCommunityIcons"
                    name="chevron-right"
                    size={22}
                    color="#ccc"
                  />
                </button>
              ))}
            </div>

            {endReached && visibleSocieties.length > 0 && (
              <div className="py-4 text-center text-xs text-[#888]">{t("End of results")}</div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SocietiesPage;